import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationType } from '../../core/services/notification-type';
import { ArticleClass } from '../../shared/models/for-1c/get-serial-numbers-for-transfer/article-class';
import { SerialnumberClass } from '../../shared/models/for-1c/get-serial-numbers-for-transfer/serialnumber-class';
import { For1CService } from '../../shared/services/for-1c.service';
import { RequestsService } from '../../shared/services/requests.service';

class ReturnEngineerWarehouseEquipmentItem {
	serialNumber: string;
	article: string;
	nomenclature: string;
	agreementIntegraCode: string;
	deviceTypeId: number;
	deviceVendor: string;
	deviceModel: string;
	deviceServiceTypeName: string;
	quality: string;
	isSelected: boolean;
}

@Component({
	selector: 'app-return-engineer-warehouse-equipment',
	templateUrl: './return-engineer-warehouse-equipment.component.html',
	styleUrls: ['./return-engineer-warehouse-equipment.component.scss']
})
export class ReturnEngineerWarehouseEquipmentComponent implements OnInit, OnDestroy {
	public equipment: ReturnEngineerWarehouseEquipmentItem[] = [];
	public searchText = '';
	public loading = false;
	public saving = false;
	public createdRequestIds: number[] = [];

	private destroy$ = new Subject<boolean>();

	constructor(
		public dialog: DialogRef,
		private for1CService: For1CService,
		private requestsService: RequestsService,
		private notificationService: NotificationService
	) {
	}

	public ngOnInit(): void {
		this.loadEquipment();
	}

	public ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	public get filteredEquipment(): ReturnEngineerWarehouseEquipmentItem[] {
		var search = (this.searchText || '').toLowerCase();
		var equipment = this.equipment;
		if (search) {
			equipment = this.equipment.filter(x =>
				(x.serialNumber || '').toLowerCase().indexOf(search) >= 0
				|| (x.article || '').toLowerCase().indexOf(search) >= 0
				|| (x.nomenclature || '').toLowerCase().indexOf(search) >= 0);
		}

		return this.getUniqueEquipment(equipment);
	}

	public get selectedCount(): number {
		return this.equipment.filter(x => x.isSelected).length;
	}

	public get equipmentCount(): number {
		return this.equipment.length;
	}

	public toggle(item: ReturnEngineerWarehouseEquipmentItem): void {
		if (this.saving || this.loading) {
			return;
		}

		item.isSelected = !item.isSelected;
	}

	public cancel(): void {
		if (this.saving) {
			return;
		}

		this.dialog.close();
	}

	public continue(): void {
		var selected = this.equipment.filter(x => x.isSelected);
		if (selected.length === 0) {
			this.notificationService.error({
				title: 'Ошибка',
				message: 'Выберите оборудование для возврата',
				notificationType: NotificationType.Toast
			});
			return;
		}

		var itemWithoutAgreement = selected.filter(x => !x.agreementIntegraCode)[0];
		if (itemWithoutAgreement) {
			this.notificationService.error({
				title: 'Ошибка',
				message: 'У выбранного оборудования отсутствует договор. Создание заявки невозможно.',
				notificationType: NotificationType.SweetAlert
			});
			return;
		}

		var itemWithoutDeviceType = selected.filter(x => !x.deviceTypeId)[0];
		if (itemWithoutDeviceType) {
			this.notificationService.error({
				title: 'Ошибка',
				message: 'У выбранного оборудования отсутствует тип устройства. Создание заявки невозможно.',
				notificationType: NotificationType.SweetAlert
			});
			return;
		}

		this.saving = true;
		this.requestsService.returnEngineerWarehouseEquipment({ items: selected })
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => this.saving = false))
			.subscribe(result => {
				if (!result.isSuccessful) {
					return;
				}

				this.createdRequestIds = result.data || [];
				this.equipment.forEach(x => x.isSelected = false);
			});
	}

	private loadEquipment(): void {
		this.loading = true;
		this.for1CService.getEngineerWarehouseSerialNumbersForTransfer()
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => this.loading = false))
			.subscribe(result => {
				this.equipment = this.getUniqueEquipment(
					this.flattenEquipment(result && result.data ? result.data : []));
			});
	}

	private flattenEquipment(data: any[]): ReturnEngineerWarehouseEquipmentItem[] {
		var result: ReturnEngineerWarehouseEquipmentItem[] = [];

		data.forEach(dataItem => {
			var articles = dataItem && dataItem.articles ? dataItem.articles : [];
			articles.forEach((article: ArticleClass) => {
				var serialNumbers = article && article.serialNumbers ? article.serialNumbers : [];
				serialNumbers.forEach((serial: SerialnumberClass) => {
					result.push(this.createEquipmentItem(article, serial));
				});
			});
		});

		return result;
	}

	private getUniqueEquipment(equipment: ReturnEngineerWarehouseEquipmentItem[]): ReturnEngineerWarehouseEquipmentItem[] {
		var seenKeys = {};
		var uniqueEquipment: ReturnEngineerWarehouseEquipmentItem[] = [];

		equipment.forEach(item => {
			var uniqueKey = this.getEquipmentUniqueKey(item);
			if (uniqueKey && seenKeys[uniqueKey]) {
				return;
			}

			if (uniqueKey) {
				seenKeys[uniqueKey] = true;
			}

			uniqueEquipment.push(item);
		});

		return uniqueEquipment;
	}

	private getEquipmentUniqueKey(item: ReturnEngineerWarehouseEquipmentItem): string {
		var serialNumber = item && item.serialNumber ? item.serialNumber.trim() : '';
		if (serialNumber) {
			return 'sn:' + serialNumber.toUpperCase();
		}

		var nomenclature = item && item.nomenclature ? item.nomenclature.trim() : '';
		if (nomenclature) {
			return 'nom:' + nomenclature.toUpperCase();
		}

		return null;
	}

	private createEquipmentItem(article: ArticleClass, serial: SerialnumberClass): ReturnEngineerWarehouseEquipmentItem {
		var device = article ? article.device : null;
		return {
			serialNumber: serial ? serial.serialNumber : null,
			article: article ? article.article : null,
			nomenclature: article ? article.nomenclature : null,
			agreementIntegraCode: serial ? serial.agreementIntegraCode : null,
			deviceTypeId: device ? device.deviceType : null,
			deviceVendor: device ? device.vendor : null,
			deviceModel: device ? device.model : null,
			deviceServiceTypeName: serial ? serial.deviceServiceTypeName : null,
			quality: serial ? serial.quality : null,
			isSelected: false
		};
	}
}
